import { Box, Button, Flex, Stack, Text } from '@chakra-ui/react';
import { WaitingPaymentsSummary } from '@diamond/shared/types';
import Image from 'next/image';

export type WaitingPaymentStickyIntent =
  | { kind: 'instruction'; orderMetaId: string }
  | { kind: 'proof_draft'; orderMetaId: string }
  | { kind: 'unpaid_list' };

export interface WaitingPaymentStickyModel {
  count: number;
  intent: WaitingPaymentStickyIntent;
}

export interface WaitingPaymentStickyProps {
  model: WaitingPaymentStickyModel;
  onAction: (intent: WaitingPaymentStickyIntent) => void;
}

const ACTION_LABEL_BY_INTENT: Record<
  WaitingPaymentStickyIntent['kind'],
  string
> = {
  instruction: 'Unggah Bukti',
  proof_draft: 'Lanjutkan',
  unpaid_list: 'Lihat Pesanan',
};

export function toWaitingPaymentStickyModel(
  summary: WaitingPaymentsSummary
): WaitingPaymentStickyModel | null {
  if (!Number.isInteger(summary.count) || summary.count <= 0) {
    return null;
  }

  if (summary.count >= 2 || summary.single_order === null) {
    return {
      count: summary.count,
      intent: {
        kind: 'unpaid_list',
      },
    };
  }

  if (summary.single_order.payment_sub_state === 'instruction') {
    return {
      count: summary.count,
      intent: {
        kind: 'instruction',
        orderMetaId: summary.single_order.order_meta_id,
      },
    };
  }

  if (summary.single_order.payment_sub_state === 'proof_draft') {
    return {
      count: summary.count,
      intent: {
        kind: 'proof_draft',
        orderMetaId: summary.single_order.order_meta_id,
      },
    };
  }

  return null;
}

export function WaitingPaymentSticky({
  model,
  onAction,
}: WaitingPaymentStickyProps) {
  return (
    <Flex
      as="section"
      role="region"
      aria-label="Pesanan menunggu pembayaran"
      position="relative"
      alignItems="center"
      justifyContent="space-between"
      w="100%"
      h="64px"
      px="16px"
      overflow="hidden"
      borderRadius="8px"
      background="linear-gradient(90deg, #e85714 0%, #ff783a 100%)"
      boxShadow={[
        '0 1px 4px rgba(12, 12, 13, 0.05)',
        '0 1px 4px rgba(12, 12, 13, 0.10)',
      ].join(',')}
    >
      <Flex alignItems="center" gap="8px" minW="0" flex="1" mr="12px">
        <Box
          position="relative"
          flexShrink="0"
          w="56px"
          h="56px"
          overflow="hidden"
        >
          <Image
            src="/images/waiting-payment-reminder.svg"
            alt=""
            aria-hidden="true"
            width={56}
            height={56}
          />
        </Box>

        <Stack spacing="0" color="white" flexShrink="0">
          <Text
            fontSize="14px"
            fontWeight="400"
            lineHeight="17px"
            whiteSpace="nowrap"
          >
            {model.count} Pesanan
          </Text>
          <Text
            fontSize="14px"
            fontWeight="400"
            lineHeight="17px"
            whiteSpace="nowrap"
          >
            Menunggu Pembayaran
          </Text>
        </Stack>
      </Flex>

      <Button
        variant="link"
        flexShrink="0"
        minW="fit-content"
        h="auto"
        p="0"
        color="white"
        fontSize="14px"
        fontWeight="600"
        lineHeight="17px"
        whiteSpace="nowrap"
        _hover={{ color: 'white', textDecoration: 'underline' }}
        _focusVisible={{
          outline: '2px solid white',
          outlineOffset: '3px',
        }}
        onClick={() => onAction(model.intent)}
      >
        {ACTION_LABEL_BY_INTENT[model.intent.kind]}
      </Button>

      <Box
        data-testid="waiting-payment-sticky-brand-border"
        aria-hidden="true"
        position="absolute"
        right="0"
        bottom="0"
        left="0"
        h="2px"
        pointerEvents="none"
        background="linear-gradient(90deg, #ff1e1e 18.27%, #2451ce 70.67%, #5ac8ff 100%)"
      />
    </Flex>
  );
}
